import { useState, useEffect } from 'react';

/**
 * Interface for window dimensions
 */
export interface WindowSize {
  width: number;
  height: number;
}

/**
 * Custom hook to get and track window dimensions
 * Useful for responsive confetti sizing
 * 
 * @returns WindowSize object with current width and height
 * 
 * @example
 * ```tsx
 * const { width, height } = useWindowSize();
 * 
 * return (
 *   <DrawWizardConfetti 
 *     width={width} 
 *     height={height} 
 *   />
 * );
 * ```
 */
export const useWindowSize = (): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: typeof window !== 'undefined' ? window.innerWidth : 800,
    height: typeof window !== 'undefined' ? window.innerHeight : 600,
  });

  useEffect(() => {
    // Handler to call on window resize
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
};
